/**
 * PipelineRow — FTP's centred pill pipeline (.stages-pipeline): every live
 * stage the selected stage doesn't hide, as a pill in pipeline order, with an
 * arrow after each pill whose "show arrow" is on (none after the last, as the
 * plan progress bar draws it). The row is the drop target that shows a hidden
 * stage again; the round "…" button is the drop target that hides one (FTP's
 * .stages-hidden-button). Side by side it sits fixed at the right edge instead
 * of following the last pill.
 *
 * The page owns the pills' DndContext, so a pill can travel between this row
 * and HiddenStagesPanel.
 *
 * When the page is narrow (vertical; the page decides from its own width) the
 * same DOM runs top to bottom (L3): pills in a column, each arrow turned to
 * point down, the "…" last under them. Drag and the floating popover work the
 * same in both directions.
 *
 * On a Locked page (plan 2026-10-10-001, U7) nothing can be hidden, so the
 * "…" drop target is not shown.
 */
import { useDndContext, useDroppable } from '@dnd-kit/core';
import { ChevronRight, MoreHorizontal } from 'lucide-react';
import { useAdminPageLevel } from '@/components/admin/AdminPageLevelContext';
import { Spinner } from '@/components/ui/Spinner';
import { cn } from '@/lib/utils';
import { PILL_DROP } from './pillDnd';
import { StagePill } from './StagePill';
import type { PillModel } from './stagePipelineModel';

export interface PillCallbacks {
  /** The one pill whose settings are open, by stage id. */
  openPillId: number | null;
  onOpenPillChange: (stageId: number, open: boolean) => void;
  onRuleChange: (toStageId: number, patch: { moveTo?: boolean; showArrow?: boolean }) => void;
}

export interface PipelineRowProps extends PillCallbacks {
  pills: PillModel[];
  loading?: boolean;
  /** Top to bottom, for a narrow page. */
  vertical?: boolean;
}

/** FTP's round "…" button: drop a pill on it to hide that stage. */
export function HideDropButton() {
  const { setNodeRef, isOver } = useDroppable({ id: PILL_DROP.hide });
  const { active } = useDndContext();
  return (
    <div
      ref={setNodeRef}
      data-testid="hide-drop"
      role="img"
      aria-label="Hide a stage: drop its pill here"
      title="Drag a stage here to hide it"
      className={cn(
        'flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#bbb] text-white transition-all',
        active && 'ring-2 ring-[#3a7fd5]/40 ring-offset-2',
        isOver && 'scale-110 bg-[#3a7fd5]',
      )}
    >
      <MoreHorizontal className="h-5 w-5" aria-hidden />
    </div>
  );
}

export function PipelineRow({ pills, loading = false, vertical = false, openPillId, onOpenPillChange, onRuleChange }: PipelineRowProps) {
  const { setNodeRef, isOver } = useDroppable({ id: PILL_DROP.row });
  const locked = useAdminPageLevel() === 'locked';
  return (
    // Side by side: the pills centred across the width, and the "…" fixed at
    // the right edge, vertically centred with the pills. It no longer follows
    // the pills, so it stays put while a pipeline loads (user, 2026-10-14: it
    // jumped to the centre and back), and its slot is only its own width, so
    // the pipeline keeps the room.
    <div className={cn('flex items-center gap-3 px-4 py-6', vertical && 'flex-col justify-center py-4')}>
      <div
        ref={setNodeRef}
        data-testid="pipeline-row"
        aria-label="Pipeline"
        className={cn(
          'flex min-h-[3.25rem] min-w-0 flex-1 flex-wrap items-center justify-center gap-y-3 rounded-lg px-2 py-2 transition-colors',
          vertical && 'max-w-full flex-none flex-col flex-nowrap gap-y-0',
          isOver && 'bg-blue-50 ring-1 ring-[#3a7fd5]/40',
        )}
      >
        {loading ? (
          <Spinner size="sm" label="Loading the pipeline…" />
        ) : (
          pills.map((p, i) => (
            <div key={p.stage.id} className={cn('flex max-w-full items-center', vertical && 'flex-col')}>
              <StagePill
                pill={p}
                open={openPillId === p.stage.id}
                onOpenChange={(open) => onOpenPillChange(p.stage.id, open)}
                onChange={(patch) => onRuleChange(p.stage.id, patch)}
                className="max-w-full"
              />
              {p.rule.showArrow && i < pills.length - 1 ? (
                <ChevronRight
                  data-testid="pill-arrow"
                  className={cn('mx-0.5 h-4 w-4 shrink-0 text-slate-400', vertical && 'mx-0 my-1 rotate-90')}
                  aria-hidden
                />
              ) : (
                i < pills.length - 1 && <span className={cn('mx-0.5 w-4 shrink-0', vertical && 'mx-0 my-1 h-4')} aria-hidden />
              )}
            </div>
          ))
        )}
      </div>
      {!locked &&
        (vertical ? (
          <HideDropButton />
        ) : (
          <div data-testid="hide-drop-column" className="flex shrink-0 justify-end">
            <HideDropButton />
          </div>
        ))}
    </div>
  );
}
